<script setup>
import {computed} from 'vue';
import {Head, Link} from '@inertiajs/vue3';
import JetAuthenticationCardLogo from '@/Jetstream/AuthenticationCardLogo.vue';
import LanguageSelector from '@/Components/LanguageSelector.vue';

const props = defineProps({
  status: Number,
});

// Mirrors resources/views/errors/*.blade.php — keep both in sync.
const messages = computed(() => ({
  403: {
    title: 'Access denied',
    message: 'You do not have permission to view this page.',
  },
  404: {
    title: 'Page not found',
    message: 'The page you requested does not exist or has been moved.',
  },
  419: {
    title: 'Your session has expired',
    message: 'For your security you have been logged out. Please log in again.',
  },
  429: {
    title: 'Too many requests',
    message: 'You have made too many attempts. Please wait a moment and try again.',
  },
  500: {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Our team has been notified.',
  },
  503: {
    title: "We'll be back shortly",
    message: 'The portal is currently undergoing maintenance. Please try again in a few minutes.',
  },
}[props.status] ?? {
  title: 'Something went wrong',
  message: 'An unexpected error occurred. Our team has been notified.',
}));

const showContact = computed(() => props.status >= 500);

const goBack = () => window.history.back();
</script>

<template>
  <Head :title="$t(messages.title)"/>

  <!--
    Mirrors Jetstream/AuthenticationCard.vue, but at a 60/40 split with a wider content
    column. Kept local rather than widening AuthenticationCard, which Login and the other
    auth pages share.
  -->
  <div class="min-h-screen bg-white flex">
    <div class="w-full lg:w-3/5 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-16 xl:px-24 shadow-xl">
      <div class="mx-auto w-full max-w-md">
        <JetAuthenticationCardLogo/>

        <div class="mt-10">
          <div class="text-7xl font-light leading-none text-secondary-500">{{ status }}</div>

          <h3 class="mt-6 text-3xl font-extrabold text-primary-500">{{ $t(messages.title) }}</h3>

          <div class="mt-4 mb-6 text-sm text-gray-600">{{ $t(messages.message) }}</div>

          <div class="flex flex-col items-start">
            <Link href="/"
                  class="inline-flex items-center px-4 py-2 bg-secondary-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 transition">
              {{ $t('Back to overview') }}
            </Link>

            <button type="button" @click="goBack"
                    class="mt-4 underline text-sm text-gray-600 hover:text-gray-900">
              {{ $t('Back') }}
            </button>

            <div v-if="showContact" class="mt-6 text-sm text-gray-600">
              {{ $t('If the problem persists, please contact us:') }}
              <a href="mailto:info@hqam.ch" class="text-primary-500 hover:underline">info@hqam.ch</a>
            </div>
          </div>
        </div>
      </div>

      <div class="mx-auto pt-5">
        <LanguageSelector/>
      </div>
    </div>

    <div class="hidden lg:block relative w-2/5 shadow-inner">
      <img class="absolute inset-0 h-full w-full object-cover" src="/images/banner/auth_banner.jpg" alt="">
    </div>
  </div>
</template>
